import React, { useState } from 'react';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { API_BASE } from '../utils/apiEndpoints';
import { LIMITS, validatePassword } from '../utils/validation.ts';

type Props = {
  t: (key: string) => string;
};

function translateServerError(t: (key: string) => string, raw: string): string {
  if (raw.startsWith('settings.') || raw.startsWith('registration.')) {
    const translated = t(raw);
    return translated === raw ? raw : translated;
  }
  return raw;
}

export function ChangePasswordSettings({ t }: Props) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!currentPassword) {
      setError(t('settings.passwordCurrentRequired'));
      return;
    }
    const lengthErr = validatePassword(newPassword);
    if (lengthErr) {
      setError(t('settings.passwordLength'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t('settings.passwordMismatch'));
      return;
    }
    if (currentPassword === newPassword) {
      setError(t('settings.passwordSame'));
      return;
    }

    try {
      setSaving(true);
      const response = await fetch(`${API_BASE}/api/profile/password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ currentPassword, newPassword, confirmPassword }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(translateServerError(t, data.error || data.message || t('settings.passwordChangeFailed')));
        return;
      }
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setSuccess(true);
    } catch {
      setError(t('settings.passwordChangeFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="settings-form settings-password-form" autoComplete="off">
      <div className="settings-section">
        <h2 className="section-title">{t('settings.passwordTitle')}</h2>
        <p className="settings-password-intro">{t('settings.passwordIntro')}</p>
        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{t('settings.passwordChanged')}</div>}
        <div className="settings-grid-3">
          <div className="settings-field">
            <label className="settings-field-label" htmlFor="current-password">
              {t('settings.passwordCurrent')}
            </label>
            <div className="settings-password-wrap">
              <input
                id="current-password"
                type={showCurrent ? 'text' : 'password'}
                className="settings-input"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoComplete="current-password"
                maxLength={LIMITS.PASSWORD_MAX}
              />
              <button
                type="button"
                className="settings-password-toggle"
                onClick={() => setShowCurrent((v) => !v)}
                aria-label={showCurrent ? t('login.hidePassword') : t('login.showPassword')}
              >
                {showCurrent ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>
          <div className="settings-field">
            <label className="settings-field-label" htmlFor="new-password">
              {t('settings.passwordNew')}
            </label>
            <div className="settings-password-wrap">
              <input
                id="new-password"
                type={showNew ? 'text' : 'password'}
                className="settings-input"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                maxLength={LIMITS.PASSWORD_MAX}
              />
              <button
                type="button"
                className="settings-password-toggle"
                onClick={() => setShowNew((v) => !v)}
                aria-label={showNew ? t('login.hidePassword') : t('login.showPassword')}
              >
                {showNew ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>
          <div className="settings-field">
            <label className="settings-field-label" htmlFor="confirm-password">
              {t('settings.passwordConfirm')}
            </label>
            <div className="settings-password-wrap">
              <input
                id="confirm-password"
                type={showConfirm ? 'text' : 'password'}
                className="settings-input"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                maxLength={LIMITS.PASSWORD_MAX}
              />
              <button
                type="button"
                className="settings-password-toggle"
                onClick={() => setShowConfirm((v) => !v)}
                aria-label={showConfirm ? t('login.hidePassword') : t('login.showPassword')}
              >
                {showConfirm ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>
        </div>
        <div className="form-actions settings-password-actions">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? t('settings.saving') : t('settings.passwordSave')}
          </button>
        </div>
      </div>
    </form>
  );
}
